/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Formats a date into a standardized string format
 * @param date - The date to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDate = (date: Date | string | undefined): string => {
  if (!date) return 'Not specified';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date into a human-readable string
 * @param date - The date to format
 * @returns Formatted date string in Month Day, Year format
 */
export const formatDateReadable = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formats a date and time into a standardized string format
 * @param date - The date to format
 * @returns Formatted date and time string in YYYY-MM-DD HH:mm:ss format
 */
export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Checks if a date is valid
 * @param date - The date to check
 * @returns True if the date is valid, false otherwise
 */
export const isValidDate = (date: Date | string): boolean => {
  const d = new Date(date);
  return !isNaN(d.getTime());
};

/**
 * Formats a date as the value of an `<input type="datetime-local">`: local time, minute precision.
 * (`toISOString()` would show UTC, and the input reads its value back as local time.)
 * @param date - The date to format
 * @returns `YYYY-MM-DDTHH:mm` in local time
 */
export const toDateTimeLocalValue = (date: Date | string): string => {
  const d = new Date(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Parses a date string into a Date object
 * @param dateString - The date string to parse
 * @returns Date object or null if parsing fails
 */
export const parseDate = (dateString: string): Date | null => {
  const d = new Date(dateString);
  return isValidDate(d) ? d : null;
};

/**
 * Converts a date for backend API - converts null to undefined
 * @param date - The date to convert
 * @returns Date object or undefined (never null)
 */
export const convertDateForBackend = (date: Date | null): Date | undefined => {
  return date || undefined;
};
